import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { Attribute } from '~/services/api-utils.server';

export default function Sizes({
  title,
  sizes,
  selectedSize,
  onSizeChange,
  unavailableSizes,
  disableUnavailable = false,
}: {
  title?: string;
  sizes: Attribute[];
  selectedSize: string;
  onSizeChange: React.MouseEventHandler<HTMLButtonElement>;
  unavailableSizes: string[];

  disableUnavailable?: boolean;
}) {
  const label = title ?? `Size${sizes.length > 1 ? 's' : ''}:`;

  return (
    <>
      <Typography variant="subtitle2" color={'text.secondary'} mr={1}>
        {label}
      </Typography>
      <Stack direction="row" alignItems="center" sx={{ flexWrap: 'wrap' }} gap={1} mt={1}>
        {sizes.map((size: Attribute) => (
          <Tooltip key={size.name} title={size.name === 'OSFA' ? 'One size fits all' : ''} placement="top" arrow>
            <span>
              <Size
                key={size.name}
                size={size}
                selected={selectedSize === size.name}
                unavailable={unavailableSizes.includes(size.name)}
                onSizeChange={onSizeChange}
                disabled={unavailableSizes.includes(size.name) && disableUnavailable}
              />
            </span>
          </Tooltip>
        ))}
      </Stack>
    </>
  );
}

export function Size({
  size,
  selected,
  unavailable,
  onSizeChange,
  readOnly,
  disabled,
}: {
  size: Attribute;
  selected?: boolean;
  unavailable?: boolean;
  onSizeChange?: React.MouseEventHandler<HTMLButtonElement>;
  readOnly?: boolean;
  disabled?: boolean;
}) {
  const selectedColor = selected ? 'text.primary' : unavailable ? 'text.disabled' : 'primary.main';

  if (readOnly) {
    return (
      <Typography
        variant="caption"
        fontSize={13}
        minWidth={40}
        minHeight={30}
        borderRadius={1}
        border="1px solid #009DC880"
        display="grid"
        color={selectedColor}
        sx={{ placeItems: 'center' }}
      >
        {size.name}
      </Typography>
    );
  }

  return (
    <Button
      variant="outlined"
      size="small"
      sx={{
        p: 0,
        minHeight: 30,
        minWidth: 40,
        display: 'grid',
        placeItems: 'center',
        color: selectedColor,
        borderColor: selectedColor,
        borderStyle: unavailable ? 'dashed' : 'solid',
        cursor: disabled ? 'default' : 'pointer',
        '&:hover': {
          color: selectedColor,
          borderColor: selectedColor,
          borderStyle: unavailable ? 'dashed' : 'solid',
        },
      }}
      name="size"
      value={size.name}
      onClick={selected || disabled ? undefined : onSizeChange}
    >
      {size.name}
    </Button>
  );
}
